import { formatTRY } from "@/lib/currency";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";
import { useEffect, useState, useMemo, useRef } from "react";
import {
  Plus,
  Minus,
  Clock,
  ChevronUp,
  ChevronDown,
  ClipboardList,
  Split,
  CreditCard,
  Settings,
  X,
  LogOut,
  LayoutDashboard,
  Store,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  UserCheck,
  AlertTriangle,
} from "lucide-react";

import { toast } from "sonner";

import {
  adisyonLabel,
  useCart,
  type Table,
  type TransferMode,
} from "@/components/dashboard/cart-context";
import { TransferModeDialog, TransferTargetDialog } from "@/components/dashboard/table-transfer";
import { FloorGeometryLayer } from "@/components/dashboard/floor-geometry";
import { EMPTY_LAYOUT } from "@/components/dashboard/floor-layout";
import { DashboardStatusBar } from "@/components/dashboard/status-bar";
import { TableCells } from "@/components/dashboard/table-cells";
import { TableVisual } from "@/components/dashboard/table-visual";
import { ConnectionSettingsModal } from "@/components/dashboard/connection-settings-modal";
import logo from "@/assets/logo.png";
import * as m from "@/paraglide/messages.js";

import { z } from "zod";

const tablesSearchSchema = z.object({
  activeFloor: z.string().catch("Zemin Kat").default("Zemin Kat"),
  statusFilter: z
    .enum(["all", "available", "occupied", "reserved", "cleaning", "maintenance"])
    .catch("all")
    .default("all"),
  searchQuery: z.string().catch("").default(""),
});

export const Route = createFileRoute("/_auth/tables")({
  validateSearch: tablesSearchSchema,
  component: TablesComponent,
});

const floorNameMap: Record<string, () => string> = {
  "Zemin Kat": m.tables_floor_ZeminKat,
  Bahçe: m.tables_floor_Bahce,
  Teras: m.tables_floor_Teras,
  "Ana Salon": m.tables_floor_AnaSalon,
};

function getFloorName(floor: string) {
  return floorNameMap[floor]?.() ?? floor;
}

const statusLabelMap: Record<string, () => string> = {
  all: m.tables_statusAll,
  available: m.tables_statusAvailable,
  occupied: m.tables_statusOccupied,
  reserved: m.tables_statusReserved,
  cleaning: m.tables_statusCleaning,
  maintenance: m.tables_statusMaintenance,
};

function getStatusLabel(status: string) {
  return statusLabelMap[status]?.() ?? status;
}

function TablesComponent() {
  const { session } = Route.useRouteContext();
  const { activeFloor, statusFilter, searchQuery } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const cart = useCart();
  const userName = session.data?.user.name ?? "Liam Basil";

  const setFilter = (updates: Partial<z.infer<typeof tablesSearchSchema>>) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...updates,
      }),
    });
  };

  // Time & Date State
  const [now, setNow] = useState(() => new Date());
  const queryClient = useQueryClient();
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);
  const [isReservationsListOpen, setIsReservationsListOpen] = useState(false);
  const [reservations, setReservations] = useState<{
    id: string;
    tableId: string;
    tableName: string;
    name: string;
    time: string;
    date: string;
    guests: number;
    note?: string;
  }[]>(() => [
    {
      id: "res-1",
      tableId: "table-3",
      tableName: "Masa 3",
      name: "Ahmet Yılmaz",
      time: "20:30",
      date: new Date().toISOString().split("T")[0],
      guests: 4,
      note: "Pencere kenarı tercih ediyor."
    },
    {
      id: "res-2",
      tableId: "table-7",
      tableName: "Masa 7",
      name: "Elif Demir",
      time: "21:00",
      date: new Date().toISOString().split("T")[0],
      guests: 2,
      note: "Yıl dönümü kutlaması."
    }
  ]);
  const [reserveTableId, setReserveTableId] = useState("");
  const [reserveName, setReserveName] = useState("");
  const [reserveTime, setReserveTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [reserveGuests, setReserveGuests] = useState(2);
  const [reserveDate, setReserveDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [reserveNote, setReserveNote] = useState("");

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  const monthNames = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Mon is 0

    const blanks = Array(startDayOfWeek).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
    return [...blanks, ...days];
  }, [calendarMonth]);

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const parts = dateStr.split("-");
      if (parts.length !== 3) return dateStr;
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const dateOptions = useMemo(() => {
    const today = new Date();
    const tomorrow = new Date(Date.now() + 86400000);

    const nextSaturday = new Date();
    const daysToSat = (6 - today.getDay() + 7) % 7;
    nextSaturday.setDate(today.getDate() + (daysToSat === 0 ? 7 : daysToSat));

    const nextSunday = new Date();
    const daysToSun = (7 - today.getDay() + 7) % 7;
    nextSunday.setDate(today.getDate() + (daysToSun === 0 ? 7 : daysToSun));

    const formatDateVal = (d: Date) => d.toISOString().split("T")[0];

    return [
      { label: "Bugün", value: formatDateVal(today) },
      { label: "Yarın", value: formatDateVal(tomorrow) },
      { label: "Cmt", value: formatDateVal(nextSaturday) },
      { label: "Pz", value: formatDateVal(nextSunday) },
    ];
  }, []);

  const availableTablesOnFloor = useMemo(() => {
    return cart.tables.filter((t) => t.floor === activeFloor && t.status === "available");
  }, [cart.tables, activeFloor]);

  const getTableOccupiedDuration = useMemo(() => {
    return (table: Table) => {
      if (table.status !== "occupied" || !table.openedAt) return null;
      const adisyonlar = cart.tableAdisyonlar[table.id] ?? [];
      const hasItems = adisyonlar.some((a) => (a.items ?? []).length > 0);
      if (hasItems) return null;
      const diffMs = now.getTime() - new Date(table.openedAt).getTime();
      return Math.max(0, Math.floor(diffMs / 1000));
    };
  }, [now, cart.tableAdisyonlar]);

  const delayedTables = useMemo(() => {
    return cart.tables
      .map((t) => {
        const duration = getTableOccupiedDuration(t);
        return { table: t, duration };
      })
      .filter((item): item is { table: Table; duration: number } => item.duration !== null && item.duration >= 15)
      .sort((a, b) => b.duration - a.duration);
  }, [cart.tables, getTableOccupiedDuration]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Floor plan zoom level (visual only, matches the real seating plan canvas)
  const [zoom, setZoom] = useState(1);

  // Panning State for zoomed-in map dragging
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Reset pan when active floor changes
  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [activeFloor]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoom <= 1) return;
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("input")) return;

    setIsDragging(true);
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    setPan({ x: newX, y: newY });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Reels-style vertical paging: the map card scrolls through one floor per
  // full-height "page" — scrolling down moves to the next floor, and picking
  // a floor from the sidebar snaps the same scroller to that page.
  const floorScrollRef = useRef<HTMLDivElement>(null);
  const isProgrammaticFloorScroll = useRef(false);

  // Selected Table inside selection screen details panel
  const [selectedTableLocal, setSelectedTableLocal] = useState<Table | null>(null);

  // Which adisyon (check) of the selected table the details panel is focused on.
  const [selectedAdisyonLocal, setSelectedAdisyonLocal] = useState<string | null>(null);

  // Popup card anchor — stores the clicked element's viewport rect for smart positioning
  const [popupPos, setPopupPos] = useState<{ top: number; bottom: number; left: number; right: number; width: number; height: number } | null>(null);



  // Sync back local table details when cart tables state changes
  const activeSelectedTable = useMemo(() => {
    if (!selectedTableLocal) return null;
    return cart.tables.find((t) => t.id === selectedTableLocal.id) ?? selectedTableLocal;
  }, [cart.tables, selectedTableLocal]);

  // List of Floors dynamically from cart
  const floors = useMemo(() => {
    return Array.from(new Set(cart.tables.map((t) => t.floor)));
  }, [cart.tables]);

  const matchingTables = useMemo(() => {
    if (!searchQuery) return [];
    return cart.tables.filter((t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, cart.tables]);

  // Sync initial scroll position on mount or whenever floors are loaded
  const initialScrollSynced = useRef(false);
  useEffect(() => {
    if (initialScrollSynced.current) return;
    const el = floorScrollRef.current;
    const index = floors.indexOf(activeFloor);
    if (el && index >= 0 && el.clientHeight > 0) {
      el.scrollTop = index * el.clientHeight;
      initialScrollSynced.current = true;
    }
  }, [floors, activeFloor]);

  // Jump the reels-style scroller to a given floor's page (used by the
  // sidebar list, the side dots, and the up/down nav buttons).
  function scrollToFloor(floor: string) {
    const el = floorScrollRef.current;

    // URL durumunu anında güncelle
    setFilter({ activeFloor: floor });

    if (el) {
      const targetFloorEl = el.querySelector(`[data-floor-id="${floor}"]`);
      if (targetFloorEl) {
        isProgrammaticFloorScroll.current = true;

        // CSS Scroll Snapping çakışmasını engellemek için geçici olarak devre dışı bırak
        el.style.scrollSnapType = "none";

        targetFloorEl.scrollIntoView({ behavior: "smooth", block: "nearest" });

        window.setTimeout(() => {
          if (el) el.style.scrollSnapType = "y mandatory";
          isProgrammaticFloorScroll.current = false;
        }, 600);
      }
    }
  }

  // While the user scrolls the map card, detect which floor "page" is
  // currently in view and sync the sidebar/state to match.
  function handleFloorScroll() {
    if (isProgrammaticFloorScroll.current) return;
    const el = floorScrollRef.current;
    if (!el || el.clientHeight === 0) return;
    const index = Math.round(el.scrollTop / el.clientHeight);
    const floor = floors[Math.min(Math.max(index, 0), floors.length - 1)];
    if (floor && floor !== activeFloor) {
      setFilter({ activeFloor: floor });
    }
  }

  function stepFloor(direction: 1 | -1) {
    const index = floors.indexOf(activeFloor);
    const nextIndex = Math.min(Math.max(index + direction, 0), floors.length - 1);
    if (floors[nextIndex]) {
      scrollToFloor(floors[nextIndex]);
    }
  }

  // A table on the active floor "matches" the current filters — non-matching
  // tables stay in place on the floor plan but render dimmed.
  function tableMatchesFilters(table: Table) {
    if (statusFilter !== "all" && table.status !== statusFilter) return false;
    if (searchQuery && !table.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }


  function handleNavigateToOrder(table: Table) {
    cart.selectTable(table.id);
    navigate({ to: "/dashboard" });
  }

  // Select a whole (unsplit) table for the details panel.
  function handleTableTap(table: Table, e: React.MouseEvent) {
    if (suppressClick.current) return;
    if (isReserveModalOpen) {
      if (table.status !== "available") {
        toast.error("Bu masa rezerve edilemez (boş değil).");
        return;
      }
      setReserveTableId(table.id);
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopupPos({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height });
    setSelectedTableLocal(table);
    setSelectedAdisyonLocal(null);
  }

  // Select one cell of a split table for the details panel.
  function handleCellTap(table: Table, adisyonId: string, e: React.MouseEvent) {
    if (suppressClick.current) return;
    if (isReserveModalOpen) {
      toast.error("Bölünmüş masalar doğrudan rezerve edilemez.");
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopupPos({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height });
    setSelectedTableLocal(table);
    setSelectedAdisyonLocal(adisyonId);
  }

  // Open the order screen for a specific adisyon (or the table's only one).
  function handleOpenOrder(table: Table, adisyonId: string | null) {
    cart.selectTable(table.id);
    if (adisyonId) cart.selectAdisyon(adisyonId);
    navigate({ to: "/dashboard" });
  }

  // Go straight to payment for a specific adisyon.
  function handlePayAdisyon(table: Table, adisyonId: string | null) {
    cart.selectTable(table.id);
    if (adisyonId) cart.selectAdisyon(adisyonId);
    navigate({ to: "/payment" });
  }

  // "Masayı Ayır" — each press adds one more adisyon (2 → 3 → 4 …).
  function handleSplitMore(table: Table) {
    const count = (cart.tableAdisyonlar[table.id] ?? []).length || 1;
    cart.splitTable(table.id, count + 1);
  }

  // Drop one check off a split table. Only ever offered for an empty adisyon:
  // collapsing checks that still hold items would silently lump strangers'
  // orders together, so those have to be paid or moved somewhere first.
  function handleCloseAdisyon(table: Table, adisyonId: string) {
    cart.closeAdisyon(table.id, adisyonId);
    setSelectedAdisyonLocal(null);
  }

  // ── Adisyon transfer ──────────────────────────────────────────────────
  // Guests change seats, so a check has to follow them. A table is dragged
  // across the floor plan onto another one; a split table is dragged one cell
  // at a time, which leaves the rest of the split behind (three checks becomes
  // two). Long-press, right-click and the details panel all reach the same
  // transfer through a target list instead.

  // The check(s) a drag is carrying, following the pointer.
  const [drag, setDrag] = useState<{
    fromTableId: string;
    adisyonId: string | null;
    label: string;
    total: number;
    x: number;
    y: number;
    overTableId: string | null;
  } | null>(null);
  // Set when a transfer was started without a drop target in mind.
  const [transferSource, setTransferSource] = useState<{
    tableId: string;
    adisyonId: string | null;
  } | null>(null);
  // A drop onto a busy table, waiting on the merge/split/swap decision.
  const [pendingTransfer, setPendingTransfer] = useState<{
    fromTableId: string;
    adisyonId: string | null;
    toTableId: string;
  } | null>(null);
  // A finished drag or long-press must not also land as a tap on the table.
  const suppressClick = useRef(false);

  function consumeClick() {
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
  }

  // The adisyonlar a transfer carries: one check, or the table's whole load.
  function carriedAdisyonlar(tableId: string, adisyonId: string | null) {
    const adisyonlar = cart.tableAdisyonlar[tableId] ?? [];
    return adisyonId ? adisyonlar.filter((a) => a.id === adisyonId) : adisyonlar;
  }

  // "S-07" for a whole table, "S-07 · B" for one check of a split one.
  function movingLabel(tableId: string, adisyonId: string | null) {
    const table = cart.tables.find((t) => t.id === tableId);
    if (!table) return "";
    const adisyonlar = cart.tableAdisyonlar[tableId] ?? [];
    const index = adisyonId ? adisyonlar.findIndex((a) => a.id === adisyonId) : -1;
    return index >= 0 && adisyonlar.length > 1
      ? `${table.name} · ${adisyonLabel(index)}`
      : table.name;
  }

  function movingTotal(tableId: string, adisyonId: string | null) {
    return carriedAdisyonlar(tableId, adisyonId).reduce(
      (sum, a) => sum + a.items.reduce((s, item) => s + item.price * item.quantity, 0),
      0,
    );
  }

  // Hit-test through the DOM rather than the floor plan's percent grid, so the
  // map's zoom and pan transforms are accounted for for free.
  function tableIdAtPoint(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    return el?.closest<HTMLElement>("[data-table-id]")?.dataset.tableId ?? null;
  }

  function startTransferDrag(e: React.PointerEvent, table: Table, adisyonId: string | null) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // An empty table has no check to carry — leave it to plain taps.
    if (carriedAdisyonlar(table.id, adisyonId).flatMap((a) => a.items).length === 0) return;

    const origin = { x: e.clientX, y: e.clientY };
    const label = movingLabel(table.id, adisyonId);
    const total = movingTotal(table.id, adisyonId);
    let started = false;

    function cleanup() {
      window.clearTimeout(longPress);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
    }

    function handleMove(ev: PointerEvent) {
      if (!started) {
        // Below the threshold this is still a tap, not a drag.
        if (Math.hypot(ev.clientX - origin.x, ev.clientY - origin.y) < 8) return;
        started = true;
        window.clearTimeout(longPress);
      }
      const over = tableIdAtPoint(ev.clientX, ev.clientY);
      setDrag({
        fromTableId: table.id,
        adisyonId,
        label,
        total,
        x: ev.clientX,
        y: ev.clientY,
        overTableId: over && over !== table.id ? over : null,
      });
    }

    function handleUp(ev: PointerEvent) {
      cleanup();
      if (!started) return;
      setDrag(null);
      consumeClick();
      const over = tableIdAtPoint(ev.clientX, ev.clientY);
      if (over && over !== table.id) requestTransfer(table.id, adisyonId, over);
    }

    function handleCancel() {
      cleanup();
      setDrag(null);
    }

    // Holding still opens the target list — the touch counterpart of a
    // right-click. Moving first cancels it and the drag wins.
    const longPress = window.setTimeout(() => {
      cleanup();
      consumeClick();
      setTransferSource({ tableId: table.id, adisyonId });
    }, 500);

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleCancel);
  }

  function handleTransferMenu(e: React.MouseEvent, table: Table, adisyonId: string | null) {
    e.preventDefault();
    if (carriedAdisyonlar(table.id, adisyonId).flatMap((a) => a.items).length === 0) return;
    setTransferSource({ tableId: table.id, adisyonId });
  }

  // A free table takes the check straight away; a busy one needs a decision.
  function requestTransfer(fromTableId: string, adisyonId: string | null, toTableId: string) {
    const busy = (cart.tableAdisyonlar[toTableId] ?? []).some((a) => a.items.length > 0);
    if (busy) {
      setPendingTransfer({ fromTableId, adisyonId, toTableId });
      return;
    }
    cart.transferAdisyon({ fromTableId, adisyonId, toTableId, mode: "move" });
    followTransfer(toTableId);
  }

  // Keep the details panel on the check the waiter just moved.
  function followTransfer(tableId: string) {
    const table = cart.tables.find((t) => t.id === tableId);
    if (table) setSelectedTableLocal(table);
    setSelectedAdisyonLocal(null);
  }

  function confirmTransfer(mode: TransferMode, toAdisyonId: string | null) {
    if (!pendingTransfer) return;
    cart.transferAdisyon({ ...pendingTransfer, toAdisyonId, mode });
    followTransfer(mode === "swap" ? pendingTransfer.fromTableId : pendingTransfer.toTableId);
    setPendingTransfer(null);
  }

  function pickTransferTarget(toTableId: string) {
    if (!transferSource) return;
    const { tableId, adisyonId } = transferSource;
    setTransferSource(null);
    requestTransfer(tableId, adisyonId, toTableId);
  }

  function handleStartNewWalkinOrder() {
    // Find the first available table on Ground Floor / activeFloor and start order
    const firstAvailable =
      cart.tables.find((t) => t.status === "available" && t.floor === activeFloor) ??
      cart.tables.find((t) => t.status === "available");

    if (firstAvailable) {
      handleNavigateToOrder(firstAvailable);
    }
  }

  const initials = userName
    .split(" ")
    .map((part: string) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Adisyonlar of the selected table, and the one the details panel focuses on.
  const selectedAdisyonlar = useMemo(() => {
    if (!activeSelectedTable) return [];
    return cart.tableAdisyonlar[activeSelectedTable.id] ?? [];
  }, [activeSelectedTable, cart.tableAdisyonlar]);

  const isSelectedSplit = selectedAdisyonlar.length > 1;

  const focusAdisyon = useMemo(() => {
    if (selectedAdisyonLocal) {
      return selectedAdisyonlar.find((a) => a.id === selectedAdisyonLocal) ?? selectedAdisyonlar[0];
    }
    return selectedAdisyonlar[0];
  }, [selectedAdisyonlar, selectedAdisyonLocal]);

  // Items shown in the details panel: the focused adisyon (whole table when it
  // has a single adisyon).
  const selectedTableOrderItems = focusAdisyon?.items ?? [];
  const focusTotal = selectedTableOrderItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  // Tables the open transfer dialogs are talking about.
  const transferSourceTable = cart.tables.find((t) => t.id === transferSource?.tableId) ?? null;
  const pendingFromTable = cart.tables.find((t) => t.id === pendingTransfer?.fromTableId) ?? null;
  const pendingToTable = cart.tables.find((t) => t.id === pendingTransfer?.toTableId) ?? null;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#f4f5f6] font-sans antialiased text-gray-900">
      <style>{`
        @keyframes scale-pulse-custom {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .animate-scale-pulse {
          animation: scale-pulse-custom 1.2s ease-in-out infinite;
        }
      `}</style>
      {searchQuery && (
        <div className="fixed inset-0 z-30 bg-transparent" onClick={() => setFilter({ searchQuery: "" })} />
      )}
      {/* Top Header Bar */}
      <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-gray-200/80 bg-white px-4 shadow-sm z-20">
        {/* Left: Hamburger menu & Search bar */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsLeftSidebarOpen(true)}
            className="flex size-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-all shrink-0"
          >
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
              <rect width="18" height="2" rx="1" fill="currentColor" />
              <rect y="6" width="13" height="2" rx="1" fill="currentColor" />
              <rect y="12" width="18" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>

          <div className="relative z-40 flex items-center border border-gray-200 bg-gray-50 rounded-xl px-2.5 py-1.5 focus-within:border-gray-400 focus-within:bg-white transition-all w-36 sm:w-56 shadow-sm">
            <Search className="size-3.5 text-gray-400 mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Masa ara..."
              value={searchQuery || ""}
              onChange={(e) => setFilter({ searchQuery: e.target.value })}
              className="w-full text-xs font-semibold bg-transparent border-none outline-none text-gray-800 placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setFilter({ searchQuery: "" })}
                className="p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X className="size-3" />
              </button>
            )}

            {/* Autocomplete Dropdown */}
            {searchQuery && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider pl-1.5">
                    Eşleşen Masalar ({matchingTables.length})
                  </span>
                </div>
                {matchingTables.length > 0 ? (
                  <div className="py-1">
                    {matchingTables.map((table) => (
                      <button
                        key={table.id}
                        onClick={() => {
                          scrollToFloor(table.floor);
                          setSelectedTableLocal(table);
                          setFilter({ searchQuery: "" });

                          setTimeout(() => {
                            const element = document.querySelector(`[data-table-id="${table.id}"]`);
                            if (element) {
                              const rect = element.getBoundingClientRect();
                              setPopupPos({
                                top: rect.top,
                                bottom: rect.bottom,
                                left: rect.left,
                                right: rect.right,
                                width: rect.width,
                                height: rect.height,
                              });
                            }
                          }, 600);
                        }}
                        className="w-full flex items-center justify-between px-3.5 py-2 hover:bg-gray-50 text-left transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-900">{table.name}</span>
                          <span className="text-[9px] font-semibold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded-full">
                            {table.floor}
                          </span>
                        </div>
                        <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${table.status === "occupied"
                          ? "bg-rose-50 text-rose-600 border-rose-100"
                          : table.status === "reserved"
                            ? "bg-amber-50 text-amber-600 border-amber-100"
                            : table.status === "cleaning"
                              ? "bg-blue-50 text-blue-600 border-blue-100"
                              : "bg-emerald-50 text-emerald-600 border-emerald-100"
                          }`}>
                          {getStatusLabel(table.status)}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-xs font-bold text-gray-400">Eşleşen masa bulunamadı</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Center: Logo */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
          <img src={logo} alt="Vidyon Logo" className="h-7 w-auto object-contain" />
          <span className="text-base font-extrabold tracking-tight text-gray-900">POS</span>
        </div>

        {/* Right: Floor pills */}
        <div className="flex items-center gap-1.5">
          {floors.map((floor) => {
            const isActive = activeFloor === floor;
            return (
              <button
                key={floor}
                onClick={() => scrollToFloor(floor)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${isActive
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
              >
                {getFloorName(floor)}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Panel layout */}
      <div className="flex flex-1 overflow-hidden p-5 gap-5">
        {isReserveModalOpen && (
          <aside className="w-80 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 flex flex-col justify-between shrink-0 animate-slide-in-left h-full max-h-full">
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Title */}
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3 shrink-0">
                <span className="text-sm font-black text-gray-900">Rezervasyon Ekle</span>
                <button
                  onClick={() => setIsReserveModalOpen(false)}
                  className="flex size-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100"
                >
                  <X className="size-3.5" />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <div className="flex-1 overflow-y-auto pr-1 space-y-4 py-1">
                {/* Table selection */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Masa Seçin
                  </label>
                  <select
                    value={reserveTableId}
                    onChange={(e) => setReserveTableId(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900"
                  >
                    <option value="">-- Masa Seçin --</option>
                    {availableTablesOnFloor.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.capacity} Kişilik)
                      </option>
                    ))}
                  </select>
                </div>

                {/* İsim - Soyisim */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    İsim - Soyisim
                  </label>
                  <input
                    type="text"
                    placeholder="Müşteri Adı Soyadı"
                    value={reserveName}
                    onChange={(e) => setReserveName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </div>

                {/* Kişi Sayısı */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Kişi Sayısı
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={reserveGuests}
                    onChange={(e) => setReserveGuests(Number(e.target.value))}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </div>

                {/* Tarih */}
                <div className="relative">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Tarih
                  </label>
                  <div className="grid grid-cols-4 gap-1.5 mb-2">
                    {dateOptions.map((opt) => {
                      const isActive = reserveDate === opt.value;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => {
                            setReserveDate(opt.value);
                            setIsDatePickerOpen(false);
                          }}
                          className={`py-1.5 text-[10px] font-bold rounded-lg border transition-all ${isActive
                            ? "bg-gray-900 border-gray-900 text-white"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsDatePickerOpen(!isDatePickerOpen);
                      setIsTimePickerOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all text-left"
                  >
                    <span>{formatDateDisplay(reserveDate) || "Tarih Seçin"}</span>
                    <Calendar className="size-3.5 text-gray-400" />
                  </button>

                  {/* Custom White Theme Calendar Popover */}
                  {isDatePickerOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 z-50 bg-white border border-gray-200/80 rounded-2xl shadow-2xl p-3.5 space-y-3 animate-slide-in-right">
                      {/* Nav Header */}
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            const prev = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
                            setCalendarMonth(prev);
                          }}
                          className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all"
                        >
                          <ChevronLeft className="size-3.5" />
                        </button>
                        <span className="text-xs font-extrabold text-gray-800">
                          {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
                            setCalendarMonth(next);
                          }}
                          className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all"
                        >
                          <ChevronRight className="size-3.5" />
                        </button>
                      </div>

                      {/* Day Name Headers (Mon to Sun) */}
                      <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-black text-gray-400 uppercase tracking-wider">
                        <span>Pt</span>
                        <span>Sa</span>
                        <span>Ça</span>
                        <span>Pe</span>
                        <span>Cu</span>
                        <span>Ct</span>
                        <span>Pz</span>
                      </div>

                      {/* Days Grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day, idx) => {
                          if (!day) return <div key={`empty-${idx}`} />;
                          const dayStr = day.toISOString().split("T")[0];
                          const isSelected = reserveDate === dayStr;
                          const isToday = new Date().toISOString().split("T")[0] === dayStr;

                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const isPast = day < today;

                          return (
                            <button
                              key={dayStr}
                              type="button"
                              disabled={isPast}
                              onClick={() => {
                                setReserveDate(dayStr);
                                setIsDatePickerOpen(false);
                              }}
                              className={`flex aspect-square items-center justify-center rounded-lg text-xs font-semibold transition-all ${isPast
                                ? "opacity-25 cursor-not-allowed text-gray-400"
                                : isSelected
                                  ? "bg-gray-900 text-white font-extrabold"
                                  : isToday
                                    ? "bg-gray-50 text-gray-900 border border-gray-200 font-bold"
                                    : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {day.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Saat */}
                <div className="relative">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Saat
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsTimePickerOpen(!isTimePickerOpen);
                      setIsDatePickerOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all text-left"
                  >
                    <span>{reserveTime || "Saat Seçin"}</span>
                    <Clock className="size-3.5 text-gray-400" />
                  </button>

                  {/* Custom White Theme Time Picker Popover */}
                  {isTimePickerOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 z-50 bg-white border border-gray-200/80 rounded-2xl shadow-2xl p-3 flex gap-2 h-44 animate-slide-in-right">
                      {/* Hour Column */}
                      <div className="flex-1 flex flex-col overflow-y-auto pr-1 space-y-1 scrollbar-thin">
                        <span className="text-[9px] font-bold text-gray-400 uppercase text-center mb-1 sticky top-0 bg-white py-0.5">Saat</span>
                        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => {
                          const [curH] = reserveTime.split(":");
                          const isSelected = curH === h;
                          return (
                            <button
                              key={`h-${h}`}
                              type="button"
                              onClick={() => {
                                const [, curM] = reserveTime.split(":");
                                setReserveTime(`${h}:${curM || "00"}`);
                              }}
                              className={`py-1 text-xs font-semibold rounded-lg transition-all ${isSelected
                                ? "bg-gray-900 text-white font-bold"
                                : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {h}
                            </button>
                          );
                        })}
                      </div>

                      {/* Divider line */}
                      <div className="w-px bg-gray-100 h-full" />

                      {/* Minute Column */}
                      <div className="flex-1 flex flex-col overflow-y-auto pr-1 space-y-1 scrollbar-thin">
                        <span className="text-[9px] font-bold text-gray-400 uppercase text-center mb-1 sticky top-0 bg-white py-0.5">Dakika</span>
                        {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((m) => {
                          const [, curM] = reserveTime.split(":");
                          const isSelected = curM === m;
                          return (
                            <button
                              key={`m-${m}`}
                              type="button"
                              onClick={() => {
                                const [curH] = reserveTime.split(":");
                                setReserveTime(`${curH || "12"}:${m}`);
                                setIsTimePickerOpen(false);
                              }}
                              className={`py-1 text-xs font-semibold rounded-lg transition-all ${isSelected
                                ? "bg-gray-900 text-white font-bold"
                                : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Not */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Not
                  </label>
                  <textarea
                    placeholder="Rezervasyon notları..."
                    value={reserveNote}
                    onChange={(e) => setReserveNote(e.target.value)}
                    rows={2}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 border-t border-gray-100 pt-3 shrink-0">
              <button
                onClick={() => setIsReserveModalOpen(false)}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-xs font-bold text-gray-500 hover:bg-gray-50 transition-all"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  if (!reserveTableId) {
                    toast.error("Lütfen bir masa seçin!");
                    return;
                  }
                  cart.updateTable(reserveTableId, {
                    status: "reserved",
                  });
                  // Append to reservations state
                  const targetTableName = cart.tables.find((t) => t.id === reserveTableId)?.name ?? reserveTableId;
                  const newRes = {
                    id: `res-${Date.now()}`,
                    tableId: reserveTableId,
                    tableName: targetTableName,
                    name: reserveName || "İsimsiz",
                    time: reserveTime,
                    date: reserveDate,
                    guests: reserveGuests,
                    note: reserveNote || undefined,
                  };
                  setReservations((prev) => [...prev, newRes]);

                  toast.success("Rezervasyon başarıyla eklendi.");
                  setIsReserveModalOpen(false);
                  // Reset states
                  const tNow = new Date();
                  const hh = String(tNow.getHours()).padStart(2, "0");
                  const mm = String(tNow.getMinutes()).padStart(2, "0");
                  setReserveTime(`${hh}:${mm}`);
                  setReserveTableId("");
                  setReserveName("");
                  setReserveGuests(2);
                  setReserveDate(new Date().toISOString().split("T")[0]);
                  setReserveNote("");
                }}
                className="flex-1 rounded-xl bg-gray-900 py-2.5 text-xs font-bold text-white hover:bg-gray-800 transition-all shadow-sm"
              >
                Kaydet
              </button>
            </div>
          </aside>
        )}
        {/* Full-width map area */}
        <main className="flex-1 flex flex-col min-w-0 gap-4 h-full relative">

          {/* Header Action Buttons & Waiter profile info */}
          <div className="flex justify-between items-center w-full shrink-0">
            {/* Left: Waiter profile & Delayed Tables */}
            <div className="flex items-center gap-3">
              <div className="text-lg font-black tracking-tight text-gray-900">
                <span className="text-cyan-500 font-black mr-1.5">Hoş geldin,</span>
                <span>{userName}</span>
              </div>

              {delayedTables.length > 0 && (
                <div className="flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] font-black text-amber-800 animate-in fade-in duration-200 shadow-sm">
                  <AlertTriangle className="size-3.5 animate-pulse text-amber-500" />
                  <span>Sipariş Bekleyen:</span>
                  <div className="flex gap-1.5">
                    {delayedTables.map(({ table, duration }) => (
                      <button
                        key={table.id}
                        onClick={() => {
                          scrollToFloor(table.floor);
                          setTimeout(() => {
                            const r = document.querySelector(`[data-table-id="${table.id}"]`)?.getBoundingClientRect();
                            if (r) {
                              setPopupPos({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height });
                            }
                            setSelectedTableLocal(table);
                            setSelectedAdisyonLocal(null);
                          }, 100);
                        }}
                        className={`px-2 py-0.5 rounded-lg text-[9px] font-black border transition-all hover:scale-105 active:scale-95 cursor-pointer ${
                          duration >= 30
                            ? "bg-rose-100 text-rose-700 border-rose-200 animate-pulse"
                            : "bg-amber-100 text-amber-700 border-amber-200"
                        }`}
                      >
                        {table.name} ({duration}s)
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsReserveModalOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 text-[11px] font-bold tracking-wide uppercase text-gray-700 shadow-sm transition-all cursor-pointer"
              >
                <Calendar className="size-3.5" />
                <span>Rezervasyon Ekle</span>
              </button>

              <button
                onClick={handleStartNewWalkinOrder}
                className="flex items-center gap-1.5 rounded-lg bg-gray-900 hover:bg-gray-800 px-3 py-1.5 text-[11px] font-bold tracking-wide uppercase text-white shadow-sm transition-all cursor-pointer"
              >
                <Plus className="size-3.5" />
                <span>{m.tables_newWalkinOrder()}</span>
              </button>
            </div>
          </div>

          {/* Top Map Card — reels-style: one full-height page per floor,
              scrolling down snaps to the next floor's plan. */}
          <div className="flex-1 overflow-hidden flex flex-col relative">

            <div
              ref={floorScrollRef}
              onScroll={handleFloorScroll}
              className="h-full w-full snap-y snap-mandatory overflow-y-scroll scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {floors.map((floor) => {
                const tablesOnFloor = cart.tables.filter((t) => t.floor === floor);
                const floorLayout = cart.floorLayouts[floor] ?? EMPTY_LAYOUT;
                const floorWidth = floorLayout.width ?? 20;
                const floorHeight = floorLayout.height ?? 15;

                return (
                  <div
                    key={floor}
                    data-floor-id={floor}
                    className="relative h-full w-full shrink-0 snap-start snap-always overflow-hidden flex items-center justify-center p-0"
                  >
                    <div
                      className="relative border border-gray-200/80 bg-white shadow-sm rounded-2xl overflow-hidden"
                      style={{
                        aspectRatio: `${floorWidth} / ${floorHeight}`,
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        touchAction: zoom > 1 ? "none" : "auto",
                      }}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                    >
                      <div
                        className={`relative size-full origin-center ${isDragging ? "" : "transition-transform duration-200"}`}
                        style={{
                          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                          cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                        }}
                      >
                        {/* Floor texture (grid represents 1 meter) */}
                        <div
                          className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px)]"
                          style={{
                            backgroundSize: `${100 / floorWidth}% ${100 / floorHeight}%`,
                          }}
                        />

                        {/* Shop geometry drawn in the admin floor-plan editor */}
                        <FloorGeometryLayer
                          layout={floorLayout}
                          tables={tablesOnFloor}
                          tableAdisyonlar={cart.tableAdisyonlar}
                          selectedAdisyonId={activeSelectedTable ? selectedAdisyonLocal : null}
                          now={now}
                          selection={activeSelectedTable ? { kind: "table", id: activeSelectedTable.id } : null}
                          onTableClick={(table, e) => handleTableTap(table, e)}
                          onTableDoubleClick={(table) => handleNavigateToOrder(table)}
                          onTablePointerDown={(table, e) => startTransferDrag(e, table, null)}
                          onTableContextMenu={(table, e) => handleTransferMenu(e, table, null)}
                          onCellClick={(table, adisyonId, e) => handleCellTap(table, adisyonId, e)}
                          onCellDoubleClick={(table, adisyonId) => handleOpenOrder(table, adisyonId)}
                          onCellPointerDown={(table, adisyonId, e) => startTransferDrag(e, table, adisyonId)}
                          onCellContextMenu={(table, adisyonId, e) => handleTransferMenu(e, table, adisyonId)}
                        />

                        {/* Tables positioned exactly like the real seating plan */}
                        {tablesOnFloor.filter((t) => t.shape !== "custom" && !t.points || (cart.tableAdisyonlar[t.id] ?? []).length > 1).map((table) => {
                          const isSelected = activeSelectedTable?.id === table.id || (isReserveModalOpen && reserveTableId === table.id);
                          const dimmed = floor === activeFloor && !tableMatchesFilters(table);
                          const adisyonlar = cart.tableAdisyonlar[table.id] ?? [];
                          const isSplit = adisyonlar.length > 1;
                          const isDropTarget = drag?.overTableId === table.id;
                          const isDragSource = drag?.fromTableId === table.id;
                          const durationMins = getTableOccupiedDuration(table);
                          return (
                            <div
                              key={table.id}
                              data-table-id={table.id}
                              style={{
                                left: `${table.x}%`,
                                top: `${table.y}%`,
                                transform: `translate(-50%, -50%) scale(${(24 * (table.scale ?? 1)) / floorWidth})`,
                              }}
                              className={`absolute z-10 touch-none hover:z-20 ${isDropTarget ? "z-30" : ""
                                } ${isDragSource ? "opacity-40" : ""}`}
                            >
                              {/* Where the dragged check would land */}
                              {isDropTarget && (
                                <span className="pointer-events-none absolute -inset-0.5 animate-pulse rounded-2xl bg-blue-500/10 ring-2 ring-blue-500" />
                              )}
                              {isSplit ? (
                                <div
                                  className={`rounded-xl p-1 transition-all duration-200 ${isSelected ? "scale-105" : "hover:scale-105"} ${
                                    durationMins !== null && durationMins >= 30 ? "animate-scale-pulse" : ""
                                  }`}
                                >
                                  <TableCells
                                    name={table.name}
                                    adisyonlar={adisyonlar}
                                    selectedAdisyonId={isSelected ? selectedAdisyonLocal : null}
                                    dimmed={dimmed}
                                    draggingAdisyonId={isDragSource ? drag.adisyonId : null}
                                    onCellClick={(adisyonId, e) => handleCellTap(table, adisyonId, e)}
                                    onCellDoubleClick={(adisyonId) =>
                                      handleOpenOrder(table, adisyonId)
                                    }
                                    onCellPointerDown={(adisyonId, e) =>
                                      startTransferDrag(e, table, adisyonId)
                                    }
                                    onCellContextMenu={(adisyonId, e) =>
                                      handleTransferMenu(e, table, adisyonId)
                                    }
                                  />
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => handleTableTap(table, e)}
                                  onDoubleClick={() => handleNavigateToOrder(table)}
                                  onPointerDown={(e) => startTransferDrag(e, table, null)}
                                  onContextMenu={(e) => handleTransferMenu(e, table, null)}
                                  className={`group rounded-xl p-1 transition-all duration-200 ${isSelected ? "scale-105" : "hover:scale-105"} ${
                                    table.status === "occupied" ? "cursor-grab active:cursor-grabbing" : ""
                                  } ${durationMins !== null && durationMins >= 30 ? "animate-scale-pulse" : ""}`}
                                >
                                  <TableVisual
                                    name={table.name}
                                    status={table.status}
                                    shape={table.shape}
                                    dimmed={dimmed}
                                    isSelected={isSelected}
                                    durationSecs={durationMins}
                                  />
                                </button>
                              )}
                            </div>
                          );
                        })}

                        {tablesOnFloor.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <p className="text-sm font-semibold text-gray-500">
                              {m.tables_noTablesConfigured({ floor })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Floor label caption, reels-style */}
                    <div className="pointer-events-none absolute left-4 top-4 z-30 rounded-lg bg-white/90 px-3 py-1.5 shadow-sm backdrop-blur-sm">
                      <span className="text-xs font-extrabold tracking-wide text-gray-800">
                        {getFloorName(floor)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Floor page dots, reels-style vertical pager */}
            {floors.length > 1 && (
              <div className="absolute right-4 top-1/2 z-30 flex -translate-y-1/2 flex-col items-center gap-2">
                {floors.map((floor) => (
                  <button
                    key={floor}
                    onClick={() => scrollToFloor(floor)}
                    title={floor}
                    className={`rounded-full transition-all ${floor === activeFloor
                      ? "h-6 w-1.5 bg-gray-900"
                      : "h-1.5 w-1.5 bg-gray-300 hover:bg-gray-400"
                      }`}
                  />
                ))}
              </div>
            )}

            {/* Up / down floor nav buttons, reels-style */}
            {floors.length > 1 && (
              <div className="absolute right-4 bottom-4 z-30 flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
                <button
                  onClick={() => stepFloor(-1)}
                  disabled={floors.indexOf(activeFloor) === 0}
                  className="flex size-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronUp className="size-3.5" />
                </button>
                <button
                  onClick={() => stepFloor(1)}
                  disabled={floors.indexOf(activeFloor) === floors.length - 1}
                  className="flex size-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronDown className="size-3.5" />
                </button>
              </div>
            )}

            {/* Zoom controls (hidden on desktop) */}
            <div className="absolute bottom-4 left-4 z-30 md:hidden flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
              <button
                onClick={() => setZoom((z) => Math.max(0.7, +(z - 0.1).toFixed(1)))}
                className="flex size-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              >
                <Minus className="size-3.5" />
              </button>
              <span className="w-10 text-center text-[11px] font-bold text-gray-500">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(1.4, +(z + 0.1).toFixed(1)))}
                className="flex size-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              >
                <Plus className="size-3.5" />
              </button>
            </div>
          </div>

        </main>
      </div>

      {/* Table popup — small card anchored near the clicked table */}
      {activeSelectedTable && popupPos && (
        <>
          {/* Invisible backdrop to close */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setSelectedTableLocal(null); setPopupPos(null); }}
          />
          {/* Popup card — smart positioned to avoid going off-screen */}
          <div
            className="fixed z-50 w-72 rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden animate-slide-in-right"
            style={(() => {
              const PW = 288; // w-72
              const PH = 240;
              const GAP = 4;
              const EDGE = 8;
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              const cx = popupPos.left + popupPos.width / 2;
              const cy = popupPos.top + popupPos.height / 2;

              const spaceTop = popupPos.top;
              const spaceBottom = vh - popupPos.bottom;
              const spaceRight = vw - popupPos.right;

              const clampX = (x: number) => Math.max(EDGE, Math.min(x, vw - PW - EDGE));
              const clampY = (y: number) => Math.max(EDGE, Math.min(y, vh - PH - EDGE));

              if (spaceBottom >= PH + GAP) {
                return { left: clampX(cx - PW / 2), top: popupPos.bottom + GAP };
              } else if (spaceTop >= PH + GAP) {
                return { left: clampX(cx - PW / 2), top: popupPos.top - GAP, transform: "translateY(-100%)" };
              } else if (spaceRight >= PW + GAP) {
                return { left: popupPos.right + GAP, top: clampY(cy - PH / 2) };
              } else {
                return { left: Math.max(EDGE, popupPos.left - GAP - PW), top: clampY(cy - PH / 2) };
              }
            })()}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div>
                <span className="text-sm font-black text-gray-900">
                  {m.topBar_table({ tableNumber: activeSelectedTable.name })}
                </span>
                <p className="text-[10px] font-semibold text-gray-400 uppercase mt-0.5">
                  {activeSelectedTable.capacity ?? 0} {m.tables_seats()} • {activeSelectedTable.area}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide border ${activeSelectedTable.status === "occupied"
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-gray-100 text-gray-600 border-gray-200"
                    }`}
                >
                  {getStatusLabel(activeSelectedTable.status)}
                </span>
                <button
                  onClick={() => { setSelectedTableLocal(null); setPopupPos(null); }}
                  className="flex size-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </div>

            {/* Order summary */}
            <div className="px-4 py-3">
              {selectedTableOrderItems.length > 0 ? (
                <div className="space-y-1.5 text-xs text-gray-600">
                  {selectedTableOrderItems.slice(0, 4).map((item) => (
                    <div key={item.productId} className="flex justify-between">
                      <span>{item.quantity}× {item.name}</span>
                      <span className="font-semibold text-gray-700">{formatTRY(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  {selectedTableOrderItems.length > 4 && (
                    <p className="text-[10px] text-gray-400">+{selectedTableOrderItems.length - 4} ürün daha</p>
                  )}
                  <div className="flex justify-between border-t border-gray-100 pt-2 font-bold text-gray-900 text-sm mt-2">
                    <span>{m.tables_amount()}</span>
                    <span className="text-rose-600">{formatTRY(focusTotal)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-1">{m.tables_noActiveBill()}</p>
              )}
            </div>
            {/* Actions */}
            <div className="flex flex-col gap-2 px-4 pb-4">
              {activeSelectedTable.status === "reserved" ? (
                <button
                  onClick={() => {
                    cart.updateTable(activeSelectedTable.id, { status: "available" });
                    setSelectedTableLocal(null);
                    setPopupPos(null);
                    toast.success("Rezervasyon kaldırıldı.");
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-rose-600 shadow-sm transition-all hover:bg-rose-100"
                >
                  <X className="size-3.5" />
                  Rezervasyonu Kaldır
                </button>
              ) : (
                <>
                  <div className="flex gap-2">
                    {activeSelectedTable.status === "available" ? (
                      <button
                        onClick={() => {
                          cart.openTable(activeSelectedTable.id);
                          setSelectedTableLocal(null);
                          setPopupPos(null);
                          toast.success(`${activeSelectedTable.name} masası açıldı.`);
                        }}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-blue-700 shadow-sm transition-all hover:bg-blue-100"
                      >
                        <UserCheck className="size-3.5" />
                        Masa Aç
                      </button>
                    ) : isSelectedSplit && focusAdisyon && selectedTableOrderItems.length === 0 ? (
                      <button
                        onClick={() => {
                          handleCloseAdisyon(activeSelectedTable, focusAdisyon.id);
                          setSelectedTableLocal(null);
                          setPopupPos(null);
                        }}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-rose-600 shadow-sm transition-all hover:bg-rose-100"
                      >
                        <X className="size-3.5" />
                        Adisyon Kapat
                      </button>
                    ) : selectedTableOrderItems.length === 0 ? (
                      <button
                        onClick={() => {
                          cart.clearTable(activeSelectedTable.id);
                          setSelectedTableLocal(null);
                          setPopupPos(null);
                          toast.success(`${activeSelectedTable.name} masası boşaltıldı.`);
                        }}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-rose-600 shadow-sm transition-all hover:bg-rose-100"
                      >
                        <X className="size-3.5" />
                        Masayı Boşalt
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePayAdisyon(activeSelectedTable, focusAdisyon?.id ?? null)}
                        disabled={selectedTableOrderItems.length === 0}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-700 shadow-sm transition-all hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <CreditCard className="size-3.5" />
                        {m.tables_payButton()}
                      </button>
                    )}
                    <button
                      onClick={() => { handleSplitMore(activeSelectedTable); setSelectedTableLocal(null); setPopupPos(null); }}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-gray-700 shadow-sm transition-all hover:bg-gray-50"
                    >
                      <Split className="size-3.5" />
                      Masayı Ayır
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      if (activeSelectedTable.status === "available") {
                        cart.openTable(activeSelectedTable.id);
                      }
                      handleOpenOrder(activeSelectedTable, focusAdisyon?.id ?? null);
                    }}
                    disabled={activeSelectedTable.status === "maintenance"}
                    className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gray-900 py-2.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm transition-all hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ClipboardList className="size-3.5" />
                    {m.tables_adisyonButton()}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Left Sidebar Menu */}
      {isLeftSidebarOpen && (
        <>
          {/* Backdrop with premium soft blur */}
          <div
            className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm transition-all"
            onClick={() => setIsLeftSidebarOpen(false)}
          />
          {/* Sidebar Drawer — Premium Floating Style */}
          <aside className="fixed top-3 left-3 bottom-3 w-[260px] bg-white/90 backdrop-blur-lg border border-gray-200/50 shadow-2xl rounded-2xl z-50 flex flex-col p-4 animate-slide-in-left">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100/60 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Vidyon Logo" className="h-6.5 w-auto object-contain" />
                <span className="text-sm font-black tracking-tight text-gray-900">POS</span>
              </div>
              <button
                onClick={() => setIsLeftSidebarOpen(false)}
                className="flex size-6 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all"
              >
                <X className="size-3.5" />
              </button>
            </div>

            {/* Nav Menu */}
            <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
              <button
                onClick={() => setIsLeftSidebarOpen(false)}
                className="flex w-full items-center gap-3 px-3 py-2 text-xs font-bold rounded-xl bg-gray-900 text-white transition-all shadow-sm shadow-gray-900/10"
              >
                <Store className="size-4" />
                <span>POS Terminali</span>
              </button>

              <Link
                to="/admin"
                className="flex items-center gap-3 px-3 py-2 text-xs font-bold rounded-xl text-gray-600 hover:bg-gray-100/70 hover:text-gray-900 transition-all"
              >
                <LayoutDashboard className="size-4" />
                <span>Yönetici Paneli</span>
              </Link>

              <button
                onClick={() => {
                  setIsLeftSidebarOpen(false);
                  setIsReservationsListOpen(true);
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-xs font-bold rounded-xl text-gray-600 hover:bg-gray-100/70 hover:text-gray-900 transition-all"
              >
                <Calendar className="size-4" />
                <span>Rezervasyonlar</span>
              </button>

              <button
                onClick={() => {
                  setIsLeftSidebarOpen(false);
                  setIsSettingsOpen(true);
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-xs font-bold rounded-xl text-gray-600 hover:bg-gray-100/70 hover:text-gray-900 transition-all"
              >
                <Settings className="size-4" />
                <span>Bağlantı Ayarları</span>
              </button>
            </nav>

            {/* Footer / User Session info */}
            <div className="border-t border-gray-100/60 pt-3 flex flex-col gap-3 shrink-0">
              <div className="flex items-center gap-2.5 px-1">
                {session.data?.user.image ? (
                  <img
                    src={session.data.user.image}
                    alt={userName}
                    className="size-8 rounded-full object-cover border border-gray-100 shadow-sm"
                  />
                ) : (
                  <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-[10px] font-bold text-white shadow-sm">
                    {initials || "U"}
                  </div>
                )}
                <div className="leading-tight">
                  <span className="block text-xs font-bold text-gray-900">{userName}</span>
                  <span className="block text-[9px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">
                    {session.data?.user.role || "Manager"}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  authClient.signOut({
                    fetchOptions: {
                      onSuccess: () => {
                        queryClient.clear();
                        navigate({
                          to: "/",
                        });
                      },
                    },
                  });
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-100 bg-rose-50/50 hover:bg-rose-100/60 py-2 text-xs font-extrabold uppercase tracking-wide text-rose-600 transition-all"
              >
                <LogOut className="size-3.5" />
                <span>Çıkış Yap</span>
              </button>
            </div>
          </aside>
        </>
      )}



      <ConnectionSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

      {isReservationsListOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-2xl p-6 w-full max-w-md max-h-[85%] flex flex-col justify-between animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="size-5 text-gray-900" />
                <span className="text-base font-black text-gray-900">Mevcut Rezervasyonlar</span>
              </div>
              <button
                onClick={() => setIsReservationsListOpen(false)}
                className="flex size-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Reservations List Scroll Area */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-3 min-h-[300px]">
              {reservations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Calendar className="size-8 text-gray-300 mb-2" />
                  <p className="text-xs font-semibold text-gray-400">Kayıtlı rezervasyon bulunmuyor.</p>
                </div>
              ) : (
                reservations.map((res) => {
                  const targetTableObj = cart.tables.find((t) => t.id === res.tableId);
                  const isTableStillReserved = targetTableObj?.status === "reserved";

                  return (
                    <div
                      key={res.id}
                      className="group rounded-xl border border-gray-100 bg-white p-3 shadow-sm hover:border-gray-200 hover:shadow-md transition-all flex flex-col gap-2"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-gray-900">{res.name}</span>
                          <span className="text-[10px] font-bold text-gray-400 mt-0.5">
                            {res.date} • {res.time} • {res.guests} Kişi
                          </span>
                        </div>
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${
                          isTableStillReserved
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}>
                          {res.tableName} {isTableStillReserved ? "(Rezerve)" : "(Kullanımda/Açık)"}
                        </span>
                      </div>

                      {res.note && (
                        <p className="text-[10px] text-gray-500 bg-gray-50 rounded-lg p-2 italic leading-relaxed">
                          Not: {res.note}
                        </p>
                      )}

                      <div className="flex gap-2 justify-end mt-1.5 border-t border-gray-50 pt-2">
                        {isTableStillReserved && (
                          <button
                            onClick={() => {
                              cart.updateTable(res.tableId, { status: "available" });
                              setReservations((prev) => prev.filter((r) => r.id !== res.id));
                              toast.success("Rezervasyon iptal edildi.");
                            }}
                            className="text-[9px] font-extrabold uppercase px-2 py-1 rounded-md text-rose-600 bg-rose-50 hover:bg-rose-100 transition-all border border-rose-100 cursor-pointer"
                          >
                            İptal Et
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setIsReservationsListOpen(false);
                            scrollToFloor(targetTableObj?.floor ?? "Zemin Kat");
                            setTimeout(() => {
                              const r = document.querySelector(`[data-table-id="${res.tableId}"]`)?.getBoundingClientRect();
                              if (r) {
                                setPopupPos({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height });
                              }
                              if (targetTableObj) {
                                setSelectedTableLocal(targetTableObj);
                                setSelectedAdisyonLocal(null);
                              }
                            }, 250);
                          }}
                          className="text-[9px] font-extrabold uppercase px-2 py-1 rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100 transition-all border border-gray-200 cursor-pointer"
                        >
                          Masayı Göster
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Add Reservation Button */}
            <div className="border-t border-gray-100 pt-4 mt-4 shrink-0">
              <button
                onClick={() => {
                  setIsReservationsListOpen(false);
                  setIsReserveModalOpen(true);
                }}
                className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-gray-900 hover:bg-gray-800 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all cursor-pointer"
              >
                <Plus className="size-4" />
                <span>Yeni Rezervasyon Ekle</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The check riding along with the pointer during a transfer drag */}
      {drag && (
        <div
          className="pointer-events-none fixed z-[200] -translate-x-1/2 -translate-y-1/2"
          style={{ left: drag.x, top: drag.y }}
        >
          <div
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 shadow-xl transition-colors ${drag.overTableId
              ? "border-blue-500 bg-blue-600 text-white"
              : "border-gray-200 bg-white text-gray-800"
              }`}
          >
            <ClipboardList className="size-4 shrink-0 opacity-70" />
            <div className="leading-tight">
              <span className="block text-xs font-extrabold">{drag.label}</span>
              <span className="block text-[10px] font-medium tabular-nums opacity-70">
                {formatTRY(drag.total)}
              </span>
            </div>
          </div>
        </div>
      )}

      {transferSource && transferSourceTable && (
        <TransferTargetDialog
          fromTable={transferSourceTable}
          movingLabel={movingLabel(transferSource.tableId, transferSource.adisyonId)}
          tables={cart.tables}
          tableAdisyonlar={cart.tableAdisyonlar}
          onPick={pickTransferTarget}
          onClose={() => setTransferSource(null)}
        />
      )}

      {pendingTransfer && pendingFromTable && pendingToTable && (
        <TransferModeDialog
          fromTable={pendingFromTable}
          toTable={pendingToTable}
          movingLabel={movingLabel(pendingTransfer.fromTableId, pendingTransfer.adisyonId)}
          toAdisyonlar={cart.tableAdisyonlar[pendingTransfer.toTableId] ?? []}
          allowSwap={pendingTransfer.adisyonId === null}
          onConfirm={confirmTransfer}
          onClose={() => setPendingTransfer(null)}
        />
      )}

      {/* Footer status bar */}
      <DashboardStatusBar />
    </div>
  );
}
